import React from 'react';
import {OperatorIntegrations, OperatorIntegrationsData} from "types/Operator";
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers} from "formik";
import FormControls from "components/FormControls";
import Button from "../../../components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import FieldHeader from "../../../components/Form/FieldHeader";
import MapStyleEditor from "./MapStyleEditor";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {PasswordField} from "../../../components/Form/PasswordField";

// Types ----

type IntegrationsFormProps = {
  settings?: OperatorIntegrations | null
  onSubmit: (values: OperatorIntegrationsData, helpers: FormikHelpers<OperatorIntegrationsData>) => any
}

// Component ----

function IntegrationsForm(props: IntegrationsFormProps) {
  const validationSchema = Yup.object().shape({
    buytickets: Yup.object().shape({
      key: Yup.string().max(255, 'Maximum of 255 characters'),
      secret: Yup.string().max(255, 'Maximum of 255 characters'),
    }),
    digitickets: Yup.object().shape({
      key: Yup.string().max(255, 'Maximum of 255 characters'),
    }),
    ticketinghub: Yup.object().shape({
      key: Yup.string().max(255, 'Maximum of 255 characters'),
    }),
    mapbox: Yup.object().shape({
      token: Yup.string().max(255, 'Maximum of 255 characters'),
      custom_styles: Yup.array().of(Yup.object().shape({
        name: Yup.string().required("This field is required"),
        url: Yup.string()
          .required("This field is required")
          .max(255, 'Maximum of 255 characters')
          .matches(/^mapbox:\/\/styles\/.+/, 'Must start with mapbox://styles/')
      })),
    })
  })

  const initialValues: OperatorIntegrationsData = {
    buytickets: {
      key: props.settings?.buytickets?.key || '',
      secret: props.settings?.buytickets?.secret || ''
    },
    digitickets: {
      key: props.settings?.digitickets?.key || ''
    },
    ticketinghub: {
      key: props.settings?.ticketinghub?.key || ''
    },
    mapbox: {
      token: props.settings?.mapbox.token || '',
      custom_styles: props.settings?.mapbox.custom_styles || []
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <section aria-labelledby="payment-details-heading">
            <Form onKeyDown={event => {
              if (event.ctrlKey && event.key === 'Enter') formik.handleSubmit()
            }}>
              <ErrorNotification formik={formik} />

              <div className="shadow sm:rounded-md sm:overflow-hidden">
                <div className="bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h2 id="payment-details-heading" className="text-lg leading-6 font-medium text-gray-900">
                      Integrations
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Configure your third-party integrations
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-6 gap-6">
                    {/* Buytickets */}
                    <div className="col-span-6">
                      <h3 className="leading-4 font-medium text-gray-900">Buytickets</h3>

                      <div className="mt-3 flex space-x-3 w-full">

                        {/* API Key */}
                        <div className="w-full">
                          <PasswordField
                            label="API Key"
                            name="buytickets.key"
                            maxLength={255}
                            placeholder="Contact Buytickets for this key"
                            lockEnabled
                          />
                        </div>

                        {/* API Secret */}
                        <div className="w-full">
                          <PasswordField
                            label="API Secret"
                            name="buytickets.secret"
                            maxLength={255}
                            placeholder="Contact Buytickets for this secret"
                            lockEnabled
                          />
                        </div>
                      </div>
                    </div>

                    {/* DigiTickets */}
                    <div className="col-span-3">
                      <h3 className="leading-4 font-medium text-gray-900">DigiTickets</h3>

                      <div className="mt-3">

                        {/* API Key */}
                        <div className="w-full">
                          <PasswordField
                            label="API Key"
                            name="digitickets.key"
                            maxLength={255}
                            placeholder="Contact DigiTickets for this key"
                            lockEnabled
                          />
                        </div>

                      </div>
                    </div>

                    {/* TicketingHub */}
                    <div className="col-span-3 col-start-1">
                      <h3 className="leading-4 font-medium text-gray-900">TicketingHub</h3>

                      <div className="mt-3">

                        {/* API Key */}
                        <div className="w-full">
                          <PasswordField
                            label="API Key"
                            name="ticketinghub.key"
                            maxLength={255}
                            placeholder="Contact TicketingHub for this key"
                            lockEnabled
                          />
                        </div>

                      </div>
                    </div>

                    {/* Mapbox */}
                    <div className="col-span-3 col-start-1">
                      <h3 className="leading-4 font-medium text-gray-900">Mapbox</h3>

                      <div className="mt-3">

                        {/* Access Token */}
                        <div className="w-full">
                          <PasswordField
                            label="Token"
                            name="mapbox.token"
                            maxLength={255}
                            placeholder="Using default mapbox token"
                            lockEnabled
                          />
                        </div>
                      </div>
                    </div>

                    {/* Custom Styles */}
                    <div className="col-span-6 -mt-2">
                      <div>
                        <FieldHeader label="Custom Map Styles"/>
                        <FieldArray name="mapbox.custom_styles">
                          {({push, remove}: FieldArrayRenderProps) => (
                            <div>
                              <ul>
                                {formik.values.mapbox?.custom_styles.map((style, index) => (
                                  <li key={index} className="my-2">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="w-full">
                                        <MapStyleEditor name={`mapbox.custom_styles.${index}`}/>
                                      </div>
                                      <div className="flex-grow-0 self-start relative top-7">
                                        <Button
                                          size={Button.sizes.TINY}
                                          color={Button.colors.WHITE}
                                          icon={TrashIcon}
                                          onClick={() => remove(index)}
                                        />
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>

                              <div className="mt-2">
                                <Button
                                  color={Button.colors.SECONDARY}
                                  icon={PlusIcon}
                                  size={Button.sizes.TINY}
                                  onClick={() => push({name: '', url: ''})}
                                >
                                  Add Map Style
                                </Button>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>
                </div>

                <FormControls formik={formik} compact withoutCancel/>
              </div>
            </Form>
          </section>
        )
      }}
    </Formik>
  );
}

// Export ----

export default IntegrationsForm;